import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Button, Card, Col, Descriptions, Dropdown, Empty, Form, Grid, Input, InputNumber, message, Modal,
  Popconfirm, Row, Select, Space, Spin, Switch, Table, Tabs, Tag, Tooltip, Typography, theme,
} from 'antd';
import {
  ArrowLeftOutlined, CodeOutlined, DeleteOutlined, EyeInvisibleOutlined, EyeOutlined, LockOutlined,
  MoreOutlined, PlusOutlined, RollbackOutlined, StopOutlined, PlayCircleOutlined, LinkOutlined,
} from '@ant-design/icons';
import { DiffEditor } from '@monaco-editor/react';
import cdnService from '../../services/cdnService';
import { userPath } from '../../../shared/config/paths';
import { formatDateTime } from '../../../shared/utils/date';
import { t, useTranslation } from '../../../shared/i18n';
import { useTheme } from '../../../theme/ThemeContext';
import WorkerLimitsTable from '../../../shared/components/cdn/WorkerLimitsTable';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const SOURCE_LABEL = { create: '创建', deploy: '部署', rollback: '回滚', import: '迁移' };
const SOURCE_COLOR = { create: 'blue', deploy: 'green', rollback: 'orange', import: 'default' };

/**
 * Worker page, modelled on Cloudflare Workers: overview (status, current
 * deployment, routes) / deployments (version history + rollback) / settings
 * (general, variables & secrets, limits, danger zone). Code is edited and tested
 * on the separate editor page (WorkerEditor).
 */
/**
 * 解析「主机名 + 路径」路由并找出它属于用户的哪个 CDN 域名(最长后缀匹配)。
 * *.example.com/api/* → {domain: 'example.com', pattern: '*.example.com/api/*'}
 */
function parseRoute(input, domains) {
  let raw = String(input || '').trim().replace(/^https?:\/\//i, '');
  if (!raw || raw.startsWith('/')) return { error: t('Please start with a hostname, e.g. example.com/* or *.example.com/api/*') };
  const slash = raw.indexOf('/');
  const hostPat = (slash === -1 ? raw : raw.slice(0, slash)).toLowerCase();
  const bare = hostPat.replace(/^\*\.?/, '');
  const match = domains
    .map((d) => String(d.domain || '').toLowerCase())
    .filter((d) => d && (bare === d || bare.endsWith(`.${d}`) || (hostPat.startsWith('*') && d.endsWith(bare) && bare.includes('.'))))
    .sort((a, b) => b.length - a.length)[0];
  if (!match) return { error: t('{{hostPat}} is not among your domains connected to the CDN', { hostPat }) };
  return { domain: match, pattern: slash === -1 ? hostPat : `${hostPat}${raw.slice(slash)}` };
}

export default function WorkerDetail() {
  const { t } = useTranslation();
  const { workerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { token } = theme.useToken();

  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [versions, setVersions] = useState([]);
  const tab = searchParams.get('tab') || 'overview';

  const load = useCallback(async () => {
    const [w, r, v] = await Promise.all([
      cdnService.getWorker(workerId),
      cdnService.getWorkerRoutes(workerId),
      cdnService.getWorkerVersions(workerId),
    ]);
    if (!w.success) {
      message.error(w.error || t('Failed to load Worker'));
      navigate(userPath('/cdn/workers'));
      return;
    }
    setWorker(w.data);
    setRoutes(r.success ? r.data || [] : []);
    setVersions(v.success ? v.data?.versions || [] : []);
    setLoading(false);
  }, [workerId, navigate, t]);

  useEffect(() => { load(); }, [load]);

  const current = useMemo(() => versions.find((v) => v.current), [versions]);

  const toggleEnabled = async () => {
    const res = await cdnService.toggleWorker(worker.id, !worker.enabled);
    if (res.success) {
      message.success(worker.enabled ? t('Disabled: this Worker no longer runs on any routes') : t('Enabled'));
      load();
    } else {
      message.error(res.error || t('Operation failed'));
    }
  };

  const removeWorker = () => {
    Modal.confirm({
      title: t('Delete Worker {{name}}?', { name: worker.name }),
      content: t('Execution will stop on all {{count}} routes. This cannot be undone.', { count: routes.length }),
      okText: t('Delete'),
      okButtonProps: { danger: true },
      cancelText: t('Cancel'),
      onOk: async () => {
        const res = await cdnService.deleteWorker(worker.id);
        if (res.success) {
          message.success(t('Deleted'));
          navigate(userPath('/cdn/workers'));
        } else {
          message.error(res.error || t('Deletion failed'));
        }
      },
    });
  };

  if (loading || !worker) {
    return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;
  }

  const editUrl = userPath(`/cdn/workers/${worker.id}/edit`);

  return (
    <div>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(userPath('/cdn/workers'))} style={{ marginBottom: 8, paddingLeft: 0 }}>
        {t('Edge functions')}
      </Button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <Space align="center" wrap>
            <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>{worker.name}</Title>
            {worker.enabled
              ? <Tag color="success">{t('Deployed · v{{version}}', { version: worker.version })}</Tag>
              : <Tag>{t('Disabled')}</Tag>}
          </Space>
          {worker.description && <Paragraph type="secondary" style={{ margin: '4px 0 0' }}>{worker.description}</Paragraph>}
        </div>
        <Space>
          <Button type="primary" icon={<CodeOutlined />} onClick={() => navigate(editUrl)}>{t('Edit code')}</Button>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                { key: 'toggle', icon: worker.enabled ? <StopOutlined /> : <PlayCircleOutlined />, label: worker.enabled ? t('Disable Worker') : t('Enable Worker') },
                { type: 'divider' },
                { key: 'delete', icon: <DeleteOutlined />, danger: true, label: t('Delete Worker') },
              ],
              onClick: ({ key }) => (key === 'toggle' ? toggleEnabled() : removeWorker()),
            }}
          >
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      </div>

      <Tabs
        activeKey={tab}
        onChange={(k) => setSearchParams(k === 'overview' ? {} : { tab: k }, { replace: true })}
        items={[
          {
            key: 'overview',
            label: t('Overview'),
            children: (
              <Overview
                worker={worker}
                current={current}
                routes={routes}
                reload={load}
                isMobile={isMobile}
                token={token}
                onEdit={() => navigate(editUrl)}
                onDeployments={() => setSearchParams({ tab: 'deployments' }, { replace: true })}
              />
            ),
          },
          {
            key: 'deployments',
            label: t('Deploy {{suffix}}', { suffix: versions.length ? ` (${versions.length})` : '' }),
            children: <Deployments worker={worker} versions={versions} reload={load} isMobile={isMobile} />,
          },
          {
            key: 'settings',
            label: t('Settings'),
            children: <Settings worker={worker} reload={load} isMobile={isMobile} onToggle={toggleEnabled} onDelete={removeWorker} />,
          },
        ]}
      />
    </div>
  );
}

// ───────────────────────────── Overview ─────────────────────────────

function Overview({ worker, current, routes, reload, isMobile, token, onEdit, onDeployments }) {
  const { t } = useTranslation();
  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={16}>
        <RoutesCard worker={worker} routes={routes} reload={reload} isMobile={isMobile} />
      </Col>
      <Col xs={24} lg={8}>
        <Card size="small" title={t('Current deployment')} extra={<Button type="link" size="small" onClick={onDeployments}>{t('All versions')}</Button>}>
          {current ? (
            <Descriptions column={1} size="small" colon={false} labelStyle={{ color: token.colorTextSecondary, width: 72 }}>
              <Descriptions.Item label={t('Version')}><Text strong>v{current.version}</Text> <Tag color={SOURCE_COLOR[current.source]}>{t(SOURCE_LABEL[current.source] || current.source)}</Tag></Descriptions.Item>
              <Descriptions.Item label={t('Description')}>{current.message || <Text type="secondary">—</Text>}</Descriptions.Item>
              <Descriptions.Item label={t('Time')}>{formatDateTime(current.created_at)}</Descriptions.Item>
              <Descriptions.Item label={t('Size')}>{(current.size / 1024).toFixed(1)} KB</Descriptions.Item>
            </Descriptions>
          ) : <Text type="secondary">{t('No deployments yet')}</Text>}
          <Button block icon={<CodeOutlined />} onClick={onEdit} style={{ marginTop: 8 }}>{t('Edit and test code')}</Button>
        </Card>

        <Card size="small" title={t('Binding & configuration')} style={{ marginTop: 16 }}>
          <Descriptions column={1} size="small" colon={false} labelStyle={{ color: token.colorTextSecondary, width: 72 }}>
            <Descriptions.Item label={t('Routes')}>{t('{{count}} entries', { count: routes.length })}</Descriptions.Item>
            <Descriptions.Item label={t('Variables')}>{t('{{count}} items', { count: (worker.vars || []).length })}</Descriptions.Item>
            <Descriptions.Item label={t('Status')}>{worker.enabled ? <Text type="success">{t('Running')}</Text> : <Text type="secondary">{t('Disabled')}</Text>}</Descriptions.Item>
            <Descriptions.Item label={t('Create')}>{formatDateTime(worker.created_at)}</Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>
    </Row>
  );
}

function RoutesCard({ worker, routes, reload, isMobile }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [domains, setDomains] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const openAdd = async () => {
    setOpen(true);
    form.setFieldsValue({ route: '', priority: 10, phase: 'request' });
    if (!domains.length) {
      const res = await cdnService.getDomains();
      const list = Array.isArray(res?.data) ? res.data : (res?.data?.domains || res?.data?.list || []);
      setDomains(list);
    }
  };

  const add = async () => {
    const v = await form.validateFields();
    const parsed = parseRoute(v.route, domains);
    if (parsed.error) {
      form.setFields([{ name: 'route', errors: [parsed.error] }]);
      return;
    }
    setSaving(true);
    const res = await cdnService.saveDomainWorker(parsed.domain, {
      worker_id: worker.id,
      route_pattern: parsed.pattern,
      priority: v.priority,
      phase: v.phase,
      enabled: true,
    });
    setSaving(false);
    if (res.success) {
      message.success(t('Route added'));
      setOpen(false);
      form.resetFields();
      reload();
    } else {
      message.error(res.error || t('Failed to add'));
    }
  };

  const toggle = async (r) => {
    const res = await cdnService.toggleDomainWorker(r.domain, r.id, !r.enabled);
    if (res.success) reload(); else message.error(res.error || t('Operation failed'));
  };

  const remove = async (r) => {
    const res = await cdnService.deleteDomainWorker(r.domain, r.id);
    if (res.success) { message.success(t('Route deleted')); reload(); } else message.error(res.error || t('Deletion failed'));
  };

  const columns = [
    {
      title: t('Routes'),
      key: 'route',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text code style={{ whiteSpace: 'nowrap' }}>{String(r.route_pattern || '').startsWith('/') ? `${r.domain}${r.route_pattern}` : r.route_pattern}</Text>
          {r.phase === 'response' && <Text type="secondary" style={{ fontSize: 12 }}>{t('Response phase')}</Text>}
        </Space>
      ),
    },
    !isMobile && { title: t('Priority'), dataIndex: 'priority', width: 80 },
    { title: t('Enable'), key: 'enabled', width: 70, render: (_, r) => <Switch size="small" checked={r.enabled} onChange={() => toggle(r)} /> },
    {
      title: '',
      key: 'actions',
      width: 48,
      render: (_, r) => (
        <Popconfirm title={t('Delete this route?')} okText={t('Delete')} cancelText={t('Cancel')} onConfirm={() => remove(r)}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ].filter(Boolean);

  return (
    <Card
      size="small"
      title={<Space><LinkOutlined />{t('Routes')}</Space>}
      extra={<Button size="small" icon={<PlusOutlined />} onClick={openAdd}>{t('Add route')}</Button>}
    >
      {!worker.enabled && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t('Worker disabled. The following routes will not execute.')} />
      )}
      {routes.length ? (
        <Table rowKey="id" size="small" pagination={false} columns={columns} dataSource={routes} scroll={{ x: true }} />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span>{t('No routes yet. Add a route to run this Worker at edge nodes for matching requests.')}</span>}
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>{t('Add route')}</Button>
        </Empty>
      )}

      <Modal title={t('Add route')} open={open} onCancel={() => setOpen(false)} onOk={add} confirmLoading={saving} okText={t('Add')} cancelText={t('Cancel')} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item
            name="route"
            label={t('Routes')}
            tooltip={t('Hostname + path; * matches any characters. No path = all paths for that hostname')}
            extra={domains.length ? t('Available domains: {{domains}}{{more}}', { domains: domains.slice(0, 4).map((d) => d.domain).join('、'), more: domains.length > 4 ? t(', etc.') : '' }) : null}
            rules={[{ required: true, message: t('Please enter a route') }]}
          >
            <Input placeholder="*.example.com/api/*" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="phase" label={t('Execution phase')}>
                <Select options={[{ value: 'request', label: t('Request phase') }, { value: 'response', label: t('Response phase') }]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="priority" label={t('Priority')} tooltip={t('When several routes match, only the highest value runs')}>
                <InputNumber min={0} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Card>
  );
}

// ───────────────────────────── Deployments ─────────────────────────────

function Deployments({ worker, versions, reload, isMobile }) {
  const { t } = useTranslation();
  const { themeMode } = useTheme();
  const [viewing, setViewing] = useState(null);
  const [code, setCode] = useState('');

  const view = async (v) => {
    const res = await cdnService.getWorkerVersion(worker.id, v.version);
    if (!res.success) { message.error(res.error || t('Failed to load')); return; }
    setCode(res.data.code || '');
    setViewing(v);
  };

  const rollback = (v) => {
    Modal.confirm({
      title: t('Roll back to v{{version}}?', { version: v.version }),
      content: t('This creates a new deployment using this version\'s code and immediately pushes it to all edge nodes serving its routes.'),
      okText: t('Roll back'),
      cancelText: t('Cancel'),
      onOk: async () => {
        const res = await cdnService.rollbackWorker(worker.id, v.version);
        if (res.success) { message.success(t('Rolled back. Current version: v{{version}}', { version: res.data.version })); reload(); } else message.error(res.error || t('Rollback failed'));
      },
    });
  };

  const columns = [
    {
      title: t('Version'),
      key: 'version',
      width: 120,
      render: (_, v) => (
        <Space>
          <Text strong>v{v.version}</Text>
          {v.current && <Tag color="success">{t('Current')}</Tag>}
        </Space>
      ),
    },
    { title: t('Type'), dataIndex: 'source', width: 80, render: (s) => <Tag color={SOURCE_COLOR[s]}>{t(SOURCE_LABEL[s] || s)}</Tag> },
    { title: t('Description'), dataIndex: 'message', ellipsis: true, render: (m) => m || <Text type="secondary">—</Text> },
    !isMobile && { title: t('Time'), dataIndex: 'created_at', width: 170, render: (t) => formatDateTime(t) },
    {
      title: '',
      key: 'actions',
      width: isMobile ? 90 : 170,
      render: (_, v) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => view(v)}>{isMobile ? t('View') : t('View code')}</Button>
          {!v.current && <Button type="link" size="small" icon={<RollbackOutlined />} onClick={() => rollback(v)}>{isMobile ? '' : t('Roll back')}</Button>}
        </Space>
      ),
    },
  ].filter(Boolean);

  return (
    <Card size="small">
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        {t('Each code deployment creates a version. Compare any version with the current code and roll back with one click.')}
      </Paragraph>
      <Table rowKey="id" size="small" columns={columns} dataSource={versions} pagination={{ pageSize: 20, hideOnSinglePage: true }} scroll={{ x: true }} />
      <Modal
        open={!!viewing}
        onCancel={() => setViewing(null)}
        width={isMobile ? '100%' : 1100}
        title={viewing ? t('Differences between v{{version}} and the current code (v{{currentVersion}})', { version: viewing.version, currentVersion: worker.version }) : ''}
        footer={viewing && !viewing.current ? [
          <Button key="close" onClick={() => setViewing(null)}>{t('Close')}</Button>,
          <Button key="rb" type="primary" icon={<RollbackOutlined />} onClick={() => { const v = viewing; setViewing(null); rollback(v); }}>{t('Roll back to this version')}</Button>,
        ] : null}
        destroyOnClose
      >
        <DiffEditor
          height="60vh"
          language="javascript"
          theme={themeMode === 'dark' ? 'vs-dark' : 'light'}
          original={code}
          modified={worker.code || ''}
          options={{ readOnly: true, renderSideBySide: !isMobile, minimap: { enabled: false }, automaticLayout: true }}
        />
      </Modal>
    </Card>
  );
}

// ───────────────────────────── Settings ─────────────────────────────

function Settings({ worker, reload, isMobile, onToggle, onDelete }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [savingGeneral, setSavingGeneral] = useState(false);

  useEffect(() => {
    form.setFieldsValue({ name: worker.name, description: worker.description });
  }, [worker, form]);

  const saveGeneral = async () => {
    const v = await form.validateFields();
    setSavingGeneral(true);
    // Saving the same code does not create a new version.
    const res = await cdnService.saveWorker({ id: worker.id, name: v.name, description: v.description, code: worker.code, enabled: worker.enabled });
    setSavingGeneral(false);
    if (res.success) { message.success(t('Saved')); reload(); } else message.error(res.error || t('Failed to save'));
  };

  const limits = worker.limits || {};

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card size="small" title={t('General')}>
        <Form form={form} layout="vertical" style={{ maxWidth: 560 }}>
          <Form.Item name="name" label={t('Name')} rules={[{ required: true, message: t('Please enter a name') }]}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="description" label={t('Description')}>
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
          <Button type="primary" onClick={saveGeneral} loading={savingGeneral}>{t('Save')}</Button>
        </Form>
      </Card>

      <VariablesCard worker={worker} reload={reload} isMobile={isMobile} />

      <Card size="small" title={t('Runtime limits')}>
        <WorkerLimitsTable limits={limits} />
        <Text type="secondary" style={{ fontSize: 12 }}>{t('Subrequests can only access ports 80/443 on public IP addresses.')}</Text>
      </Card>

      <Card size="small" title={<Text type="danger">{t('Danger zone')}</Text>}>
        <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Text strong>{worker.enabled ? t('Disable Worker') : t('Enable Worker')}</Text>
            <div><Text type="secondary">{worker.enabled ? t('Disabling immediately stops execution on all routes. Code and routes are retained.') : t('Run again on all enabled routes.')}</Text></div>
          </div>
          <Button onClick={onToggle}>{worker.enabled ? t('Disable') : t('Enable')}</Button>
        </Space>
        <div style={{ height: 16 }} />
        <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Text strong>{t('Delete Worker')}</Text>
            <div><Text type="secondary">{t('Deletes code, versions, and all route bindings. This cannot be undone.')}</Text></div>
          </div>
          <Button danger onClick={onDelete}>{t('Delete')}</Button>
        </Space>
      </Card>
    </Space>
  );
}

function VariablesCard({ worker, reload, isMobile }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState({});

  useEffect(() => {
    setRows((worker.vars || []).map((v, i) => ({ ...v, _k: `${v.name}-${i}`, _stored: v.type === 'secret' })));
  }, [worker]);

  const update = (k, patch) => setRows((rs) => rs.map((r) => (r._k === k ? { ...r, ...patch } : r)));
  const add = (type) => setRows((rs) => [...rs, { _k: `new-${Date.now()}`, name: '', type, value: '', _stored: false }]);
  const remove = (k) => setRows((rs) => rs.filter((r) => r._k !== k));

  const save = async () => {
    const bad = rows.find((r) => !/^[A-Za-z_][A-Za-z0-9_]{0,63}$/.test(r.name));
    if (bad) { message.error(t('Invalid variable name: {{name}}', { name: bad.name || t('(Empty)') })); return; }
    const missing = rows.find((r) => r.type === 'secret' && !r._stored && !r.value);
    if (missing) { message.error(t('Please enter a value for key {{name}}', { name: missing.name })); return; }
    setSaving(true);
    const res = await cdnService.saveWorkerVars(worker.id, rows.map(({ name, type, value }) => ({ name, type, value: value ?? '' })));
    setSaving(false);
    if (res.success) { message.success(t('Variables saved and pushed to the edge')); reload(); } else message.error(res.error || t('Failed to save'));
  };

  const columns = [
    {
      title: t('Variable name'),
      key: 'name',
      width: isMobile ? 140 : 240,
            // A stored secret's value is never sent back, so renaming it would save an empty value.
      render: (_, r) => <Input value={r.name} placeholder="API_TOKEN" disabled={r._stored} onChange={(e) => update(r._k, { name: e.target.value.trim() })} />,
    },
    {
      title: t('Type'),
      key: 'type',
      width: 110,
      render: (_, r) => (
        <Select
          value={r.type}
          style={{ width: '100%' }}
          disabled={r._stored}
          onChange={(type) => update(r._k, { type })}
          options={[{ value: 'plain', label: t('Text') }, { value: 'secret', label: t('Key') }]}
        />
      ),
    },
    {
      title: t('Value'),
      key: 'value',
      render: (_, r) => (r.type === 'secret' ? (
        <Input.Password
          value={r.value || ''}
          placeholder={r._stored ? t('Saved (no longer shown). Leave blank to keep it.') : t('Enter the secret value')}
          visibilityToggle={{ visible: !!reveal[r._k], onVisibleChange: (v) => setReveal((s) => ({ ...s, [r._k]: v })) }}
          iconRender={(v) => (v ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
          prefix={<LockOutlined />}
          onChange={(e) => update(r._k, { value: e.target.value })}
        />
      ) : (
        <Input value={r.value || ''} onChange={(e) => update(r._k, { value: e.target.value })} />
      )),
    },
    {
      title: '',
      key: 'del',
      width: 40,
      render: (_, r) => <Tooltip title={t('Delete')}><Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(r._k)} /></Tooltip>,
    },
  ];

  return (
    <Card
      size="small"
      title={t('Variables and secrets')}
      extra={(
        <Space>
          <Button size="small" icon={<PlusOutlined />} onClick={() => add('plain')}>{t('Variables')}</Button>
          <Button size="small" icon={<LockOutlined />} onClick={() => add('secret')}>{t('Key')}</Button>
        </Space>
      )}
    >
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        {t('In your code, use')} <Text code>{t('env.variable_name')}</Text> {t('Read, for example,')} <Text code>{'fetch(request, env) { env.API_TOKEN }'}</Text>{t('。')}
        {t('Once saved, the secret will no longer be shown and will not appear in code or version history.')}
      </Paragraph>
      {rows.length ? (
        <Table rowKey="_k" size="small" pagination={false} columns={columns} dataSource={rows} scroll={{ x: 560 }} />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No variables yet')} />
      )}
      <Button type="primary" onClick={save} loading={saving} style={{ marginTop: 12 }}>{t('Save and deploy variables')}</Button>
    </Card>
  );
}
